import { z } from "zod";
import { isReservedPortalLeafSlug, PORTAL_FAMILY_HOST_LABELS, } from "../constants/portal-hosting.js";
import { L30_PREMIUM_PORTAL_CAPABILITIES, PortalCapabilityListSchema, PortalWorkspacePackageKindSchema, PortalWorkspacePurposeSchema, } from "./portal-workspace.js";
import { PortalUserRoleSchema } from "./portal.js";
/**
 * "New partner portal" onboarding wizard — SSOT for the preview / execute
 * contract and the persisted run shape.
 *
 * Canon: docs/design-canon/04-shared/ssot.md.
 *
 * Two-phase by design. `preview` is read-only and returns the exact effects
 * the run will have; `execute` re-sends the SAME input plus the fingerprint
 * the preview issued and an exact-slug confirmation, so a mutated or stale
 * wizard state cannot provision. Every object is `.strict()` — an unknown
 * key means the client and the API disagree about the shape, and silently
 * dropping it is how a "selected document" quietly stops being copied.
 *
 * `sourceTenantSlug` is first-class: the documents an operator picks
 * generally live in the L30 tenant, not in the tenant being created, so the
 * run reads one schema and writes another. Nothing here carries a
 * source-tenant row id into a target-tenant grant — the run records the
 * source → target document mapping explicitly instead.
 */
// ─── Primitives ────────────────────────────────────────────
const TenantSlugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(63)
    .regex(/^[a-z0-9-]+$/, "Tenant slug may contain lowercase letters, digits and hyphens only");
const AccentColorSchema = z
    .preprocess((value) => (typeof value === "string" && value.trim() === "" ? null : value), z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullable())
    .default(null);
const OptionalBlobKeySchema = z
    .preprocess((value) => (typeof value === "string" && value.trim() === "" ? null : value), z.string().trim().min(1).max(1000).nullable())
    .default(null);
/**
 * Ordered run steps. The order is the contract, not a hint: invitees are
 * LAST so a partner never receives a sign-in link to a portal whose
 * workspace, project or documents failed to land.
 *
 * `workspace` provisions the portal workspace, which is also the default
 * engagement bootstrap (see provisionPortalWorkspace → ensureWorkspaceRuntime);
 * `engagement` then resolves the engagement every later grant hangs off.
 */
export const PORTAL_ONBOARDING_STEPS = [
    "tenant_schema",
    "portal_family",
    "workspace",
    "engagement",
    "project",
    "documents",
    "invitees",
];
export const PortalOnboardingStepSchema = z.enum(PORTAL_ONBOARDING_STEPS);
export const PortalOnboardingStepStatusSchema = z.enum([
    "pending",
    "running",
    "succeeded",
    "skipped",
    "failed",
]);
export const PortalOnboardingRunStatusSchema = z.enum([
    "pending",
    "running",
    "succeeded",
    "failed",
]);
/** What a planned effect does to the target. Mirrors the wizard's review copy. */
export const PortalOnboardingEffectActionSchema = z.enum(["create", "reuse", "skip", "send"]);
// ─── Input ─────────────────────────────────────────────────
export const PortalOnboardingInviteeSchema = z
    .object({
    email: z.string().trim().email().toLowerCase(),
    name: z.string().trim().min(1).max(200),
    role: PortalUserRoleSchema.default("client_member"),
})
    .strict();
/**
 * A selected source Vault document. Id only — the title, filename, size and
 * blob key are resolved server-side under `sourceTenantSlug`, so a client
 * cannot describe a document as something it is not.
 */
export const PortalOnboardingDocumentSelectionSchema = z
    .object({ sourceDocumentId: z.string().uuid() })
    .strict();
export const PortalOnboardingProjectSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).nullable().default(null),
})
    .strict();
export const PortalOnboardingInputSchema = z
    .object({
    /** Tenant to create (or reuse). Becomes the `tenant_<slug>` schema. */
    tenantSlug: TenantSlugSchema,
    /** Human-facing partner name, used for audit + wizard review copy. */
    displayName: z.string().trim().min(1).max(200),
    /** Portal workspace name shown to the client inside the portal SPA. */
    workspaceName: z.string().trim().min(1).max(200),
    portalFamily: z.enum(PORTAL_FAMILY_HOST_LABELS),
    packageKind: PortalWorkspacePackageKindSchema.default("l30_premium"),
    purpose: PortalWorkspacePurposeSchema.default("relationship"),
    capabilities: PortalCapabilityListSchema.default([...L30_PREMIUM_PORTAL_CAPABILITIES]),
    portalEnabled: z.boolean().default(true),
    accentColor: AccentColorSchema,
    logoBlobKey: OptionalBlobKeySchema,
    initialProject: PortalOnboardingProjectSchema.nullable().default(null),
    invitees: z.array(PortalOnboardingInviteeSchema).max(25).default([]),
    /** Tenant the selected documents are read FROM (typically `l30`). */
    sourceTenantSlug: TenantSlugSchema,
    documents: z.array(PortalOnboardingDocumentSelectionSchema).max(50).default([]),
})
    .strict()
    .superRefine((value, ctx) => {
    // Family labels are shells, not leaves: `devices.portal…` cannot also be
    // a tenant. `gop` is the documented migration exception.
    if (isReservedPortalLeafSlug(value.tenantSlug)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["tenantSlug"],
            message: `"${value.tenantSlug}" is a portal family host label and cannot be a tenant slug`,
        });
    }
    const seenInvitees = new Set();
    for (const [index, invitee] of value.invitees.entries()) {
        if (seenInvitees.has(invitee.email)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["invitees", index, "email"],
                message: `${invitee.email} is listed more than once`,
            });
        }
        seenInvitees.add(invitee.email);
    }
    const seenDocuments = new Set();
    for (const [index, doc] of value.documents.entries()) {
        if (seenDocuments.has(doc.sourceDocumentId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["documents", index, "sourceDocumentId"],
                message: "The same document is selected more than once",
            });
        }
        seenDocuments.add(doc.sourceDocumentId);
    }
});
export const PortalOnboardingPreviewRequestSchema = z
    .object({ input: PortalOnboardingInputSchema })
    .strict();
export const PortalOnboardingExecuteRequestSchema = z
    .object({
    input: PortalOnboardingInputSchema,
    /**
     * The operator must retype the slug exactly. Checked server-side against
     * `input.tenantSlug` — provisioning a tenant is not undoable from the UI.
     */
    confirmTenantSlug: z.string().min(1).max(63),
    /** Fingerprint the preview issued for this exact input. */
    previewFingerprint: z.string().trim().min(16).max(128),
    /**
     * Caller-stable key. Re-executing with the same key resumes the existing
     * run instead of starting a second one, so a double-click or a retried
     * fetch cannot double-provision.
     */
    idempotencyKey: z.string().trim().min(8).max(120),
})
    .strict();
// ─── Preview ───────────────────────────────────────────────
export const PortalOnboardingEffectSchema = z
    .object({
    step: PortalOnboardingStepSchema,
    action: PortalOnboardingEffectActionSchema,
    summary: z.string().min(1).max(500),
})
    .strict();
export const PortalOnboardingDocumentPreviewSchema = z
    .object({
    sourceDocumentId: z.string().uuid(),
    title: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    sourceFolderTitle: z.string().nullable(),
    action: PortalOnboardingEffectActionSchema,
    warning: z.string().nullable(),
})
    .strict();
export const PortalOnboardingInviteePreviewSchema = z
    .object({
    email: z.string().email(),
    name: z.string(),
    role: PortalUserRoleSchema,
    action: PortalOnboardingEffectActionSchema,
    warning: z.string().nullable(),
})
    .strict();
export const PortalOnboardingPreviewSchema = z
    .object({
    fingerprint: z.string().min(16),
    tenantSlug: z.string(),
    sourceTenantSlug: z.string(),
    portalFamily: z.enum(PORTAL_FAMILY_HOST_LABELS),
    canonicalPortalUrl: z.string().min(1),
    targetTenantExists: z.boolean(),
    effects: z.array(PortalOnboardingEffectSchema),
    documents: z.array(PortalOnboardingDocumentPreviewSchema),
    invitees: z.array(PortalOnboardingInviteePreviewSchema),
    warnings: z.array(z.string()),
    /** An unfinished run for this slug + idempotency key, when one exists. */
    resumableRunId: z.string().uuid().nullable(),
    generatedAt: z.string().datetime(),
})
    .strict();
export const PortalOnboardingPreviewResponseSchema = z
    .object({ preview: PortalOnboardingPreviewSchema })
    .strict();
// ─── Run ───────────────────────────────────────────────────
export const PortalOnboardingStepResultSchema = z
    .object({
    step: PortalOnboardingStepSchema,
    status: PortalOnboardingStepStatusSchema,
    detail: z.string().nullable(),
    error: z.string().nullable(),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
})
    .strict();
/**
 * Source → target document mapping. `destBlobKey` is deterministic for a
 * given (run, source document) pair, which is what makes a retry after a
 * half-finished copy safe: the same bytes land on the same key and the DB
 * row is found by that key instead of being inserted twice.
 */
export const PortalOnboardingDocumentResultSchema = z
    .object({
    sourceDocumentId: z.string().uuid(),
    title: z.string(),
    destBlobKey: z.string(),
    blobCopiedAt: z.string().datetime().nullable(),
    targetDocumentId: z.string().uuid().nullable(),
    grantId: z.string().uuid().nullable(),
    error: z.string().nullable(),
})
    .strict();
export const PortalOnboardingInviteeResultSchema = z
    .object({
    email: z.string().email(),
    name: z.string(),
    role: PortalUserRoleSchema,
    /** `created` rows are the only ones this run emails. */
    action: z.enum(["created", "existing"]),
    portalUserId: z.string().uuid().nullable(),
    inviteSentAt: z.string().datetime().nullable(),
    inviteError: z.string().nullable(),
})
    .strict();
export const PortalOnboardingRunSchema = z
    .object({
    id: z.string().uuid(),
    tenantSlug: z.string(),
    sourceTenantSlug: z.string(),
    status: PortalOnboardingRunStatusSchema,
    currentStep: PortalOnboardingStepSchema.nullable(),
    idempotencyKey: z.string(),
    previewFingerprint: z.string(),
    canonicalPortalUrl: z.string(),
    steps: z.array(PortalOnboardingStepResultSchema),
    documents: z.array(PortalOnboardingDocumentResultSchema),
    invitees: z.array(PortalOnboardingInviteeResultSchema),
    workspaceId: z.string().uuid().nullable(),
    engagementId: z.string().uuid().nullable(),
    projectId: z.string().uuid().nullable(),
    vaultFolderId: z.string().uuid().nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    createdBy: z.string(),
    createdAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    failedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const PortalOnboardingRunResponseSchema = z
    .object({ run: PortalOnboardingRunSchema })
    .strict();
export const PortalOnboardingRunListResponseSchema = z
    .object({ runs: z.array(PortalOnboardingRunSchema) })
    .strict();
export const PortalOnboardingSourceDocumentQuerySchema = z
    .object({
    sourceTenantSlug: TenantSlugSchema,
    search: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(25),
})
    .strict();
export const PortalOnboardingSourceDocumentSchema = z
    .object({
    id: z.string().uuid(),
    title: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
})
    .strict();
export const PortalOnboardingSourceDocumentListResponseSchema = z
    .object({ documents: z.array(PortalOnboardingSourceDocumentSchema) })
    .strict();
export const PortalOnboardingRunIdParamSchema = z.object({ id: z.string().uuid() }).strict();
//# sourceMappingURL=portal-onboarding.js.map